import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { clearTabComponent } from 'src/hooks/useTab';

// Tab veri yapÄ±larÄ± iÃ§in tip tanÄ±mlarÄ±
export interface SubTab {
  id: string;
  label: string;
  componentId: string;
  path?: string;
  data?: any;
  isDirty?: boolean;
  saveRequestId?: string;
  saveRequestReason?: 'close' | 'save';
}

export interface MainTab {
  id: string;
  label: string;
  componentId: string | null;
  subTabs: SubTab[];
  activeSubTab: number;
  path?: string;
  data?: any;
  isDirty?: boolean;
  saveRequestId?: string;
  saveRequestReason?: 'close' | 'save';
}

// State tip tanÄ±mÄ±
export interface TabsState {
  mainTabs: MainTab[];
  activeTabIndex: number;
  shouldUpdateUrl: boolean;
}

// Action payload tipleri
export interface AddMainTabPayload {
  id: string;
  label: string;
  componentId: string | null;
  subTabs?: SubTab[];
  activeSubTab?: number;
  path?: string;
  data?: any;
}

export interface UpdateSubTabsPayload {
  id: string;
  subTabs: SubTab[];
}

export interface SetActiveSubTabPayload {
  mainIndex: number;
  subIndex: number;
}

// UygulamanÄ±n sekme (tab) yÃ¶netimi iÃ§in baÅŸlangÄ±Ã§ state'i
const initialState: TabsState = {
  mainTabs: [],          // Ana sekmeler dizisi
  activeTabIndex: 0,     // Aktif ana sekme indexi
  shouldUpdateUrl: true  // URL gÃ¼ncellenme tercihi
};

// URL temizleyici yardÄ±mcÄ± fonksiyon
const cleanUrlPath = (path: string): string => {
  if (!path) return '/';
  let cleanPath = path.replace(/\/+/g, '/').replace(/^\/+|\/+$/g, '');
  const segments = cleanPath.split('/');
  const uniqueSegments = segments.filter((segment, index) => segments.indexOf(segment) === index);
  cleanPath = '/' + uniqueSegments.join('/');

  if (cleanPath.includes('/nurse/medcard')) {
    cleanPath = cleanPath.replace(/\/nurse\/medcard\/+/g, '/nurse/medcard/');
  }

  return cleanPath;
};

const normalizeLabel = (value: string): string =>
  (value || "")
    .replaceAll("TabelasÄ±", "Tabelası")
    .replaceAll("Tabelas�", "Tabelası")
    .replaceAll("TabelasÃ„Â±", "Tabelası")
    .replaceAll("Tabelasï¿½", "Tabelası")
    .replaceAll("Yatış ZamanÄ±", "Yatış Zamanı")
    .replaceAll("YatÄ±ÅŸ ZamanÃ„Â±", "Yatış Zamanı")
    .replaceAll("GeniÅŸlet", "Genişlet")
    .replaceAll("GeniÃ…Å¸let", "Genişlet");
// Sekme iÅŸlemleri slice'Ä±
const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    // Ana sekme ekler, varsa tekrar eklemez
    addMainTab: (state, action: PayloadAction<AddMainTabPayload>) => {
      const { id, label, componentId, subTabs, activeSubTab, path, data } = action.payload;
      const safeLabel = normalizeLabel(label);
      const existingTabIndex = state.mainTabs.findIndex(tab => tab.id === id);

      if (existingTabIndex === -1) {
        state.mainTabs.push({
          id,
          label: safeLabel,
          componentId, // Store componentId instead of component
          subTabs: (subTabs || []).map(st => ({ ...st, label: normalizeLabel(st.label) })),
          activeSubTab: activeSubTab ?? 0,
          path,
          data
        });
        state.activeTabIndex = state.mainTabs.length - 1; // Yeni sekme aktifleÅŸtirilir
      } else {
        // Tab zaten varsa, onu aktif yap
        state.activeTabIndex = existingTabIndex;

        // URL'yi gÃ¼ncelle
        const existingTab = state.mainTabs[existingTabIndex];
        let newPath = existingTab.subTabs?.length > 0
          ? existingTab.subTabs[existingTab.activeSubTab || 0]?.path
          : existingTab.path;

        if (state.shouldUpdateUrl && window.history?.pushState && newPath) {
          newPath = cleanUrlPath(newPath);
          window.history.pushState({}, '', newPath);
        }
      }
    },

    // Alt sekmeleri gÃ¼nceller ve en son alt sekmeyi aktif yapar
    updateSubTabs: (state, action: PayloadAction<UpdateSubTabsPayload>) => {
      const { id, subTabs } = action.payload;
      const tabIndex = state.mainTabs.findIndex(tab => tab.id === id);

      if (tabIndex !== -1) {
        state.mainTabs[tabIndex].subTabs = subTabs.map((subTab) => ({
          ...subTab,
          label: normalizeLabel(subTab.label),
          data: subTab.data || {}
        }));
        state.mainTabs[tabIndex].activeSubTab = subTabs.length - 1;
        state.activeTabIndex = tabIndex; // Ana sekmeyi aktifleÅŸtir
      }
    },

    // Sekme kapatÄ±r, URL'yi uygun ÅŸekilde gÃ¼nceller
    closeTab: (state, action: PayloadAction<string>) => {
      const tabId = action.payload;
      console.log('Redux closeTab çalişiyor. TabId:', tabId);
      console.log('Mevcut state:', {
        mainTabsCount: state.mainTabs.length,
        activeTabIndex: state.activeTabIndex,
        mainTabs: state.mainTabs.map(t => ({ id: t.id, label: t.label }))
      });

      const mainTabIndex = state.mainTabs.findIndex(tab => tab.id === tabId);
      console.log('Ana tab index:', mainTabIndex);

      if (mainTabIndex !== -1) {
        const componentIdToClear = state.mainTabs[mainTabIndex].componentId;
        if (componentIdToClear) {
          clearTabComponent(componentIdToClear); // Component'i bellekten sil
        }
        console.log('Ana tab siliniyor:', state.mainTabs[mainTabIndex].label);
        // Ana sekme sil
        state.mainTabs.splice(mainTabIndex, 1);

        // activeTabIndex'i gÃ¼ncelle
        if (state.mainTabs.length === 0) {
          // HiÃ§ tab kalmadÄ±ysa activeTabIndex'i -1 yap
          state.activeTabIndex = -1;
        } else if (state.activeTabIndex >= state.mainTabs.length) {
          state.activeTabIndex = state.mainTabs.length - 1;
        } else if (state.activeTabIndex === mainTabIndex) {
          if (mainTabIndex > 0) {
            state.activeTabIndex = mainTabIndex - 1;
          } else if (state.mainTabs.length > 0) {
            state.activeTabIndex = 0;
          }
        }
        console.log('Ana tab silindikten sonra state:', {
          mainTabsCount: state.mainTabs.length,
          activeTabIndex: state.activeTabIndex
        });

        if (state.mainTabs.length === 0) {
          console.log('Tum sekmeler kapali, ana sayfaya donuluyor');
          // Tum sekmeler kapaliysa ana sayfaya don
          if (state.shouldUpdateUrl && window.history?.pushState) {
            window.history.pushState({}, '', '/');
          }
        } else {
          console.log('Aktif sekme guncelleniyor');
          // Kalan aktif sekmenin path'ini URL'ye yansÄ±t
          const newActiveTab = state.mainTabs[state.activeTabIndex];
          let newPath = newActiveTab.subTabs?.length > 0
            ? newActiveTab.subTabs[newActiveTab.activeSubTab || 0]?.path
            : newActiveTab.path;

          if (state.shouldUpdateUrl && window.history?.pushState && newPath) {
            newPath = cleanUrlPath(newPath);
            window.history.pushState({}, '', newPath);
          }
        }
      } else {
        console.log('ğŸ” Alt sekme aranÄ±yor...');
        // Alt sekme silme durumu
        for (let tabIndex = 0; tabIndex < state.mainTabs.length; tabIndex++) {
          const tab = state.mainTabs[tabIndex];
          if (tab.subTabs) {
            const subTabIndex = tab.subTabs.findIndex(subTab => subTab.id === tabId);
            if (subTabIndex !== -1) {
              const componentIdToClear = tab.subTabs[subTabIndex].componentId;
              if (componentIdToClear) {
                clearTabComponent(componentIdToClear); // Alt sekme component'ini bellekten sil
              }
              console.log('âœ‚ï¸ Alt tab siliniyor:', tab.subTabs[subTabIndex].label);
              tab.subTabs.splice(subTabIndex, 1);
              if (tab.subTabs.length === 0) {
                console.log('âœ‚ï¸ Alt tab kalmadÄ±, ana tab da siliniyor');
                // Alt sekmesi kalmayan ana sekmeyi de sil
                state.mainTabs.splice(tabIndex, 1);
                if (state.activeTabIndex >= state.mainTabs.length) {
                  state.activeTabIndex = Math.max(0, state.mainTabs.length - 1);
                }

                if (state.mainTabs.length === 0) {
                  if (state.shouldUpdateUrl && window.history?.pushState) {
                    window.history.pushState({}, '', '/');
                  }
                } else {
                  const newActiveTab = state.mainTabs[state.activeTabIndex];
                  let newPath = newActiveTab.subTabs?.length > 0
                    ? newActiveTab.subTabs[newActiveTab.activeSubTab || 0]?.path
                    : newActiveTab.path;

                  if (state.shouldUpdateUrl && window.history?.pushState && newPath) {
                    newPath = cleanUrlPath(newPath);
                    window.history.pushState({}, '', newPath);
                  }
                }
              } else {
                console.log('ğŸ”„ Kalan alt tab aktif yapÄ±lÄ±yor');
                // Kalan alt sekmelerden birini aktif yap ve URL'yi gÃ¼ncelle
                tab.activeSubTab = Math.min(tab.activeSubTab, tab.subTabs.length - 1);
                let newPath = tab.subTabs[tab.activeSubTab]?.path;
                if (state.shouldUpdateUrl && window.history?.pushState && newPath) {
                  newPath = cleanUrlPath(newPath);
                  window.history.pushState({}, '', newPath);
                }
              }
              break;
            }
          }
        }
      }
      console.log('Redux closeTab tamamlandı');
    },

    // Aktif ana sekmeyi ayarlar
    setActiveTab: (state, action: PayloadAction<number>) => {
      const newIndex = action.payload;

      // GeÃ§erli index kontrolÃ¼
      if (newIndex >= 0 && newIndex < state.mainTabs.length) {
        state.activeTabIndex = newIndex;

        // URL'yi gÃ¼ncelle
        const newActiveTab = state.mainTabs[newIndex];
        let newPath = newActiveTab.subTabs?.length > 0
          ? newActiveTab.subTabs[newActiveTab.activeSubTab || 0]?.path
          : newActiveTab.path;

        if (state.shouldUpdateUrl && window.history?.pushState && newPath) {
          newPath = cleanUrlPath(newPath);
          window.history.pushState({}, '', newPath);
        }
      } else if (state.mainTabs.length === 0) {
        // HiÃ§ tab yoksa activeTabIndex'i -1 yap
        state.activeTabIndex = -1;
        if (state.shouldUpdateUrl && window.history?.pushState) {
          window.history.pushState({}, '', '/');
        }
      }
    },

    // Belirli bir ana sekmenin aktif alt sekmesini ayarlar
    setActiveSubTab: (state, action: PayloadAction<SetActiveSubTabPayload>) => {
      const { mainIndex, subIndex } = action.payload;
      if (state.mainTabs[mainIndex]) {
        state.mainTabs[mainIndex].activeSubTab = subIndex;
      }
    },

    // URL gÃ¼ncelleme tercihini ayarlar
    setUrlUpdatePreference: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdateUrl = action.payload;
    },

    // TÃ¼m sekmeleri kapatÄ±r
    closeAllTabs: (state) => {
      // BileÅŸenleri temizle
      state.mainTabs.forEach(tab => {
        if (tab.componentId) {
          clearTabComponent(tab.componentId);
        }
        tab.subTabs.forEach(subTab => {
          if (subTab.componentId) {
            clearTabComponent(subTab.componentId);
          }
        });
      });

      state.mainTabs = [];
      state.activeTabIndex = -1;

      if (state.shouldUpdateUrl && window.history?.pushState) {
        window.history.pushState({}, '', '/');
      }
    },
    // Tab label'Ä±nÄ± gÃ¼nceller
    updateTabLabel: (state, action: PayloadAction<{ id: string; label: string }>) => {
      const { id, label } = action.payload;
      const tab = state.mainTabs.find(t => t.id === id);
      if (tab) {
        tab.label = normalizeLabel(label);
      }
    },
    // Tab dirty durumunu gÃ¼nceller
    setTabDirty: (state, action: PayloadAction<{ id: string; isDirty: boolean }>) => {
      const { id, isDirty } = action.payload;
      const tab = state.mainTabs.find(t => t.id === id);
      if (tab) {
        tab.isDirty = isDirty;
        return;
      }
      // Alt sekme ara
      for (const mainTab of state.mainTabs) {
        const subTab = mainTab.subTabs?.find(st => st.id === id);
        if (subTab) {
          subTab.isDirty = isDirty;
          return;
        }
      }
    },
    // Tab save isteÄŸi gÃ¶nderir
    requestTabSave: (state, action: PayloadAction<{ id: string; requestId: string; reason?: 'close' | 'save' }>) => {
      const { id, requestId, reason } = action.payload;
      const tab = state.mainTabs.find(t => t.id === id);
      if (tab) {
        tab.saveRequestId = requestId;
        tab.saveRequestReason = reason;
        return;
      }
      // Alt sekme ara
      for (const mainTab of state.mainTabs) {
        const subTab = mainTab.subTabs?.find(st => st.id === id);
        if (subTab) {
          subTab.saveRequestId = requestId;
          subTab.saveRequestReason = reason;
          return;
        }
      }
    }
  }
});

export const {
  addMainTab,
  updateSubTabs,
  setActiveSubTab,
  closeTab,
  closeAllTabs,
  setUrlUpdatePreference,
  updateTabLabel,
  setTabDirty,
  requestTabSave,
  setActiveTab
} = tabsSlice.actions;

export default tabsSlice.reducer;